/**
 * AdminTenantModules — Modul-Verwaltung pro Mandant
 *
 * Erreichbar über /admin/modules. Zeigt alle aktivierbaren Module mit
 * Toggle-Switches. Speichern → PUT /api/admin/tenants/{tenant_id}/modules
 *
 * Super-Admins können den Mandanten über ein Dropdown wählen.
 * Mandanten-Admins arbeiten implizit auf ihrem eigenen Mandanten.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ToggleLeft, ToggleRight, Loader2, Phone, Sun, Gavel,
  RefreshCw, ShieldAlert, ServerCog, Building2, Receipt, KeyRound, CheckCircle2,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import api from "@/lib/api";

const MODULES = [
  {
    key: "telesales",
    label: "Telesales",
    desc: "Eingehende Telefon-Erfassungen, Capture-Maske, Wiedervorlagen, Director-Cockpit, Orientierungsangebote.",
    icon: Phone,
  },
  {
    key: "pv",
    label: "Photovoltaik (PV)",
    desc: "PV-Komponenten-Katalog, PV-Pakete, Wirtschaftlichkeitsrechnung im Premium-PDF.",
    icon: Sun,
  },
  {
    key: "lead_auction",
    label: "Lead-Auctions",
    desc: "Marktplatz, auf dem qualifizierte Wärmepumpen-Leads versteigert werden.",
    icon: Gavel,
  },
];

export default function AdminTenantModules() {
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]); // Super-Admin: alle Mandanten
  const [tenantId, setTenantId] = useState(null);
  const [modules, setModules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 1. User aus localStorage laden
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("novatus_user") || "null");
      setUser(u);
      // Mandanten-Admin: tenant_id aus Profil. Super-Admin: erstmal leer, kommt aus Dropdown.
      if (!u?.is_super_admin) setTenantId(u?.tenant_id || null);
    } catch { /* ignore */ }
  }, []);

  // 2. Super-Admin: Mandanten-Liste laden
  useEffect(() => {
    if (!user) return;
    if (!user.is_super_admin) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/superadmin/tenants");
        const active = (data || []).filter((t) => t.active !== false);
        setTenants(active);
        // Auto-Auswahl: ersten Mandanten vorbelegen, wenn nur einer existiert
        if (active.length === 1) setTenantId(active[0].id);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Mandanten konnten nicht geladen werden");
      } finally { setLoading(false); }
    })();
  }, [user]);

  // 3. Module für ausgewählten Mandanten laden
  const load = async (tid) => {
    if (!tid) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/tenants/${tid}/modules`);
      setModules(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modul-Status konnte nicht geladen werden");
      setModules(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (tenantId) load(tenantId); else setModules(null);
  }, [tenantId]);

  const toggle = (key) => {
    setModules((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  const save = async () => {
    if (!tenantId || !modules) return;
    setSaving(true);
    try {
      const body = {};
      for (const m of MODULES) body[m.key] = Boolean(modules[m.key]);
      const r = await api.put(`/admin/tenants/${tenantId}/modules`, body);
      setModules(r.data);
      toast.success("Modul-Konfiguration gespeichert", {
        description: "Aktive Module sind nun freigeschaltet. App-Neustart nicht nötig.",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const isSuper = !!user?.is_super_admin;
  const selectedTenantName = tenants.find((t) => t.id === tenantId)?.name;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-modules-page">
      <AppHeader title="Modul-Verwaltung" back="/admin/users" />
      <main className="max-w-3xl mx-auto px-3 sm:px-5 pt-5 pb-10">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-2xl bg-[#00E575]/15 flex items-center justify-center">
            <ServerCog className="text-[#00E575]" size={20} />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold">Modul-Verwaltung</h1>
            <p className="text-zinc-400 text-[12px] font-medium">Funktionen pro Mandant an- und ausschalten.</p>
          </div>
          <button
            onClick={() => load(tenantId)}
            disabled={loading || !tenantId}
            data-testid="modules-refresh"
            className="h-10 w-10 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-40"
            title="Neu laden"
          >
            <RefreshCw size={14} className={loading ? "animate-spin text-zinc-400" : "text-zinc-400"} />
          </button>
        </div>

        {/* Super-Admin Mandanten-Dropdown */}
        {isSuper && (
          <div className="mb-5 bg-[#18181B] border border-zinc-800 rounded-2xl p-3.5">
            <label className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1.5 flex items-center gap-1.5">
              <Building2 size={11} /> Mandant wählen
            </label>
            <select
              value={tenantId || ""}
              onChange={(e) => setTenantId(e.target.value || null)}
              data-testid="modules-tenant-select"
              className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
            >
              <option value="">— Mandant auswählen —</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
              ))}
            </select>
            {selectedTenantName && (
              <div className="text-[12px] text-zinc-300 mt-2">
                Bearbeite Module für: <span className="text-[#00E575] font-semibold">{selectedTenantName}</span>
              </div>
            )}
          </div>
        )}

        {/* Mandanten-Admin ohne tenant_id (sehr selten) */}
        {!isSuper && !tenantId && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-[12.5px] text-amber-200 mb-4 flex items-start gap-2">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <div>Kein Mandant in deinem Profil hinterlegt. Bitte als Mandanten-Admin einloggen.</div>
          </div>
        )}

        {loading && tenantId ? (
          <div className="text-center py-12"><Loader2 className="animate-spin inline text-zinc-500" /></div>
        ) : modules && tenantId ? (
          <div className="space-y-3">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const active = Boolean(modules[m.key]);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => toggle(m.key)}
                  data-testid={`module-toggle-${m.key}`}
                  className={`w-full text-left p-4 rounded-2xl border transition ${
                    active
                      ? "bg-[#00E575]/5 border-[#00E575]/40 hover:bg-[#00E575]/10"
                      : "bg-[#18181B] border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-[#00E575]/15 text-[#00E575]" : "bg-zinc-900 text-zinc-500"}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-[14.5px] text-zinc-100">{m.label}</h3>
                        <span className={`text-[12px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${active ? "bg-[#00E575]/20 text-[#00E575]" : "bg-zinc-800 text-zinc-300"}`}>
                          {active ? "aktiv" : "inaktiv"}
                        </span>
                      </div>
                      <p className="text-[12px] text-zinc-400 mt-1 leading-snug font-medium">{m.desc}</p>
                    </div>
                    {active
                      ? <ToggleRight className="text-[#00E575] shrink-0" size={32} />
                      : <ToggleLeft className="text-zinc-600 shrink-0" size={32} />}
                  </div>
                </button>
              );
            })}

            <div className="pt-3 flex justify-end gap-2">
              <button
                onClick={save}
                disabled={saving}
                data-testid="modules-save"
                className="h-11 px-5 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold inline-flex items-center gap-2 disabled:bg-zinc-800 disabled:text-zinc-500"
              >
                {saving ? <Loader2 className="animate-spin" size={14} /> : null}
                {saving ? "Speichere…" : "Speichern"}
              </button>
            </div>

            <p className="text-[10.5px] text-zinc-500 leading-snug mt-3 pt-3 border-t border-zinc-800/60">
              Hinweis: Ein deaktiviertes Modul blendet alle zugehörigen Menüpunkte sofort aus
              und blockt die zugehörigen API-Endpoints. Beim Wieder-Aktivieren sind alle Daten erhalten.
            </p>

            {/* Iteration 82 · Easybill-Integration (Buchhaltung) */}
            <EasybillSection tenantId={tenantId} />
          </div>
        ) : isSuper && !tenantId ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">
            Wähle oben einen Mandanten zur Konfiguration.
          </div>
        ) : null}
      </main>
    </div>
  );
}


/**
 * Iteration 82 · Easybill-API-Key-Konfiguration pro Mandant.
 * Kein Read-Back des Keys (Security). Nur Set + Test-Ping.
 */
function EasybillSection({ tenantId }) {
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    if (!key.trim() || key.trim().length < 10) {
      toast.error("Bitte einen gültigen Easybill-API-Key eintragen (mind. 10 Zeichen).");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/api/admin/tenants/${tenantId}/easybill`, { api_key: key.trim() });
      toast.success("Easybill-API-Key gespeichert.");
      setKey("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/api/admin/tenants/${tenantId}/easybill/test`);
      toast.success(
        data.customer_count_hint > 0
          ? `Verbindung OK — Easybill-Account mit ${data.customer_count_hint} Kunden erkannt.`
          : "Verbindung OK — Easybill-Account ist erreichbar.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verbindung fehlgeschlagen.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-zinc-800/60">
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-xl bg-blue-500/15 text-blue-300 flex items-center justify-center shrink-0">
            <Receipt size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-[14.5px] text-zinc-100">Easybill Buchhaltung</h3>
            <p className="text-[12px] text-zinc-400 mt-1 leading-snug font-medium">
              1-Click-Push von Kunde + Angebot zu Easybill. Der API-Key ist mandant-spezifisch
              und wird verschlüsselt gespeichert. Anlegen unter <span className="text-zinc-200 font-semibold">easybill.de → Einstellungen → API-Keys</span>.
            </p>
          </div>
        </div>

        <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold flex items-center gap-1 mb-1.5">
          <KeyRound size={10} /> Easybill-API-Key
        </label>
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="eyJ…  oder  eb_live_…"
          data-testid="easybill-api-key-input"
          className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-100 focus:border-[#00E575] outline-none text-[13px] font-mono"
          autoComplete="new-password"
        />
        <p className="text-[10.5px] text-zinc-500 mt-1 mb-3">
          Der bestehende Key wird beim Speichern <span className="text-zinc-300">überschrieben</span>. Aus Sicherheitsgründen wird der aktuelle Key nicht angezeigt.
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={save}
            disabled={saving || !key.trim()}
            data-testid="easybill-save-key"
            className="h-10 px-4 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12.5px] inline-flex items-center gap-1.5 disabled:bg-zinc-800 disabled:text-zinc-500"
          >
            {saving ? <Loader2 className="animate-spin" size={13} /> : <KeyRound size={13} />}
            Key speichern
          </button>
          <button
            onClick={testConn}
            disabled={testing}
            data-testid="easybill-test-connection"
            className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-[12.5px] inline-flex items-center gap-1.5 border border-zinc-700 disabled:opacity-50"
          >
            {testing ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} />}
            Verbindung testen
          </button>
        </div>
      </div>
    </div>
  );
}
